import { useUserProfile } from '@hooks';
import {
  SchoolDirectory,
  SchoolSearchFilterOptions,
  SchoolSearchFilters,
  useSchoolSearchFiltersFromUrlParams,
  useSchoolSearchResults,
} from '@school';
import { PageWrapper } from '@shared';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';

interface SchoolDirectoryPageProps {
  serverSideParams: ParsedUrlQuery | null;
}

export default function SchoolDirectoryPage({
  serverSideParams,
}: SchoolDirectoryPageProps) {
  const profile = useUserProfile();
  const router = useRouter();
  const { isLoadingFilters, filters } = useSchoolSearchFiltersFromUrlParams(
    serverSideParams ?? router.query,
  );
  const { isLoading, nextPage, results } = useSchoolSearchResults(
    filters,
    isLoadingFilters,
  );

  function updateFilters(updatedFilters: SchoolSearchFilters) {
    router.push(updatedFilters.route, undefined, { shallow: true });
  }

  function handleNextPage() {
    updateFilters(filters.withPage(nextPage));
  }

  function handleUpdateOptions(updatedOptions: SchoolSearchFilterOptions) {
    updateFilters(filters.withOptions(updatedOptions));
  }

  // TODO: does this work with the async/await?
  async function handleUpdateQuery(updatedQuery: string) {
    updateFilters(await filters.withQuery(updatedQuery));
  }

  return (
    <PageWrapper pageTitle="CNA School Directory">
      <SchoolDirectory
        filters={filters}
        hasNextPage={!!nextPage}
        isLoading={isLoading || isLoadingFilters}
        onNextPage={handleNextPage}
        onUpdateOptions={handleUpdateOptions}
        onUpdateQuery={handleUpdateQuery}
        profile={profile}
        results={results}
      />
    </PageWrapper>
  );
}

/**
 * Because of the way the search filter works, we want to make sure we always
 * have the latest query params as soon as possible instead of relying
 * solely on the hook.
 */
export const getServerSideProps: GetServerSideProps<
  SchoolDirectoryPageProps
> = async (context) =>
  Promise.resolve({ props: { serverSideParams: context.params ?? null } });
